import { useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import type { AlertRule, MetricKey } from '@iot-dashboard/shared';
import { KIND_LABELS, isCameraKind } from '@iot-dashboard/shared';
import { useAlerts, useDevice, useEvents, useReadings, useRules, useSites, useUpdateDevice } from '../../api/hooks';
import { BatteryMeter } from '../../components/BatteryMeter';
import { FormRow, TagsInput } from '../../components/Form';
import { KindIcon } from '../../components/KindIcon';
import { Panel } from '../../components/Panel';
import { SeverityBadge } from '../../components/SeverityBadge';
import { StatusPill } from '../../components/StatusDot';
import { ErrorState, Loading, errorMessage } from '../../components/States';
import { TimeAgo } from '../../components/TimeAgo';
import { TimeSeriesChart, type Point } from '../../components/TimeSeriesChart';
import { useToast } from '../../components/Toast';
import { PageHeader } from '../../layout/PageHeader';
import { useTenantContext } from '../../layout/TenantLayout';
import { formatDateTime, formatLux, formatPercent, formatSignal, formatTemperature, macWithColons } from '../../lib/format';

const RANGES = [
  { key: '6h', label: 'Last 6 hours', hours: 6 },
  { key: '24h', label: 'Last 24 hours', hours: 24 },
  { key: '7d', label: 'Last 7 days', hours: 24 * 7 },
  { key: '30d', label: 'Last 30 days', hours: 24 * 30 },
] as const;

const CHARTS: { metric: MetricKey; title: string; unit: string; format?: (v: number) => string }[] = [
  { metric: 'temperature', title: 'Temperature', unit: '°C', format: (v) => `${v.toFixed(1)} °C` },
  { metric: 'humidity', title: 'Humidity', unit: '%', format: (v) => `${v.toFixed(1)} %` },
  { metric: 'light', title: 'Ambient light', unit: 'lux', format: (v) => `${Math.round(v)} lux` },
  { metric: 'batteryPercentage', title: 'Battery', unit: '%', format: (v) => `${Math.round(v)} %` },
];

function thresholdsFor(rules: AlertRule[], deviceId: string, metric: MetricKey) {
  return rules
    .filter((r) => r.enabled && r.kind === 'threshold' && r.metric === metric && r.threshold !== undefined)
    .filter((r) => r.deviceIds.length === 0 || r.deviceIds.includes(deviceId))
    .map((r) => ({ value: r.threshold as number, label: r.name }));
}

export function SensorDetailPage() {
  const { tenantId, base, canConfigure } = useTenantContext();
  const { deviceId = '' } = useParams();
  const toast = useToast();
  const device = useDevice(tenantId, deviceId);
  const sites = useSites(tenantId);
  const rules = useRules(tenantId);
  const alerts = useAlerts(tenantId, 'open');
  const events = useEvents(tenantId, 200);
  const update = useUpdateDevice(tenantId, deviceId);
  const [range, setRange] = useState<(typeof RANGES)[number]['key']>('24h');
  const [editing, setEditing] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [notes, setNotes] = useState('');

  const hours = RANGES.find((r) => r.key === range)?.hours ?? 24;
  const [from, to] = useMemo(() => {
    const now = new Date();
    now.setSeconds(0, 0);
    return [new Date(now.getTime() - hours * 3_600_000).toISOString(), now.toISOString()];
  }, [hours]);
  const readings = useReadings(tenantId, deviceId, from, to);

  if (device.isPending) return <Loading label="Loading sensor…" />;
  if (device.isError) return <ErrorState error={device.error} retry={() => device.refetch()} />;
  const d = device.data;
  // Reached through a link that did not know the kind: hand it to the camera page.
  if (isCameraKind(d.kind)) {
    return <Navigate to={`${base}/cameras/${encodeURIComponent(d.deviceId)}`} replace />;
  }
  const m = d.metrics;
  const site = sites.data?.find((s) => s.siteId === d.siteId);
  const deviceAlerts = (alerts.data ?? []).filter((a) => a.deviceId === d.deviceId);
  const deviceEvents = (events.data ?? []).filter((e) => e.deviceId === d.deviceId).slice(0, 15);

  const series = (metric: MetricKey): Point[] =>
    (readings.data?.items ?? [])
      .filter((r) => typeof r.metrics[metric] === 'number')
      .map((r) => ({ ts: r.ts, value: r.metrics[metric] as number }));

  const startEdit = () => {
    setTags(d.tags);
    setNotes(d.notes ?? '');
    setEditing(true);
  };
  const save = async () => {
    try {
      await update.mutateAsync({ tags, notes });
      toast.push('Sensor updated', 'success');
      setEditing(false);
    } catch (err) {
      toast.push(errorMessage(err), 'error');
    }
  };

  const stateReadings: { label: string; value: string }[] = [];
  if (m.isOpened !== undefined) stateReadings.push({ label: d.mountType === 'window' ? 'Window' : d.mountType === 'garage' ? 'Garage door' : 'Door', value: m.isOpened ? 'Open' : 'Closed' });
  if (m.motionDetected !== undefined) stateReadings.push({ label: 'Motion', value: m.motionDetected ? 'Detected' : 'Clear' });
  if (m.leakDetected !== undefined) stateReadings.push({ label: 'Water leak', value: m.leakDetected ? 'DETECTED' : 'None' });
  if (m.tamperDetected !== undefined) stateReadings.push({ label: 'Tamper', value: m.tamperDetected ? 'DETECTED' : 'None' });
  if (m.alarmTriggered !== undefined) stateReadings.push({ label: 'Alarm input', value: m.alarmTriggered ? 'Triggered' : 'Idle' });

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Sensors', to: `${base}/sensors` }, { label: d.name }]}
        title={<span className="row"><KindIcon kind={d.kind} size={22} /> {d.name} <StatusPill status={d.status} /></span>}
        subtitle={`${KIND_LABELS[d.kind]}${site ? ` · ${site.name}` : ''}`}
        actions={
          <div className="filters">
            <select className="select" value={range} onChange={(e) => setRange(e.target.value as typeof range)} aria-label="Time range">
              {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>
        }
      />

      {deviceAlerts.length > 0 ? (
        <div className={`alert-banner alert-banner--${deviceAlerts.some((a) => a.severity === 'critical') ? 'critical' : 'warning'}`}>
          <div className="grow">
            <strong>{deviceAlerts.length} open alert{deviceAlerts.length === 1 ? '' : 's'}.</strong>{' '}
            {deviceAlerts.map((a) => a.message).join(' · ')}
          </div>
          <Link to={`${base}/alerts`} className="btn btn--sm">Manage alerts</Link>
        </div>
      ) : null}

      <div className="grid-3">
        <Panel title="Current readings">
          <div className="reading-grid">
            {m.temperature !== undefined ? <div className="reading"><div className="reading__label">Temperature</div><div className="reading__value">{formatTemperature(m.temperature)}</div></div> : null}
            {m.humidity !== undefined ? <div className="reading"><div className="reading__label">Humidity</div><div className="reading__value">{formatPercent(m.humidity, 1)}</div></div> : null}
            {m.light !== undefined ? <div className="reading"><div className="reading__label">Light</div><div className="reading__value">{formatLux(m.light)}</div></div> : null}
            {stateReadings.map((r) => <div key={r.label} className="reading"><div className="reading__label">{r.label}</div><div className="reading__value reading__value--state">{r.value}</div></div>)}
            {m.batteryPercentage !== undefined ? <div className="reading"><div className="reading__label">Battery</div><div className="reading__value reading__value--state"><BatteryMeter percentage={m.batteryPercentage} low={m.batteryLow} /></div></div> : null}
            {(m.signalStrength !== undefined || m.signalQuality !== undefined) ? <div className="reading"><div className="reading__label">Signal</div><div className="reading__value reading__value--state">{formatSignal(m.signalStrength, m.signalQuality)}</div></div> : null}
            {Object.keys(m).length === 0 ? <div className="muted">This device does not report readings.</div> : null}
          </div>
        </Panel>

        <Panel title="Details" actions={canConfigure && !editing ? <button type="button" className="btn btn--sm" onClick={startEdit}>Edit</button> : null}>
          <dl className="kv">
            <dt>MAC</dt><dd className="mono">{macWithColons(d.mac)}</dd>
            <dt>Firmware</dt><dd>{d.firmwareVersion ?? '—'}</dd>
            <dt>Site</dt><dd>{site ? <Link to={`${base}/overview?site=${encodeURIComponent(site.siteId)}`}>{site.name}</Link> : d.siteId}</dd>
            {d.mountType ? <><dt>Mount</dt><dd>{d.mountType}</dd></> : null}
            <dt>Connection</dt><dd>{d.connectionState ?? '—'}</dd>
            <dt>Last seen</dt><dd><TimeAgo iso={d.lastSeenAt} /></dd>
            <dt>Up since</dt><dd>{formatDateTime(d.upSince)}</dd>
            <dt>Last sync</dt><dd><TimeAgo iso={d.lastSyncAt} /></dd>
            {d.offlineSince ? <><dt>Offline since</dt><dd>{formatDateTime(d.offlineSince)}</dd></> : null}
            {d.timestamps.openStatusChangedAt ? <><dt>State changed</dt><dd>{formatDateTime(d.timestamps.openStatusChangedAt)}</dd></> : null}
            {d.timestamps.motionDetectedAt ? <><dt>Last motion</dt><dd>{formatDateTime(d.timestamps.motionDetectedAt)}</dd></> : null}
            {d.timestamps.leakDetectedAt ? <><dt>Last leak</dt><dd>{formatDateTime(d.timestamps.leakDetectedAt)}</dd></> : null}
          </dl>
        </Panel>

        <Panel title="Tags & notes" actions={editing ? <><button type="button" className="btn btn--sm" onClick={() => setEditing(false)}>Cancel</button><button type="button" className="btn btn--primary btn--sm" onClick={save} disabled={update.isPending}>Save</button></> : null}>
          {editing ? (
            <div className="form">
              <FormRow label="Tags"><TagsInput value={tags} onChange={setTags} /></FormRow>
              <FormRow label="Notes"><textarea className="textarea" value={notes} onChange={(e) => setNotes(e.target.value)} /></FormRow>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 8 }}>{d.tags.length ? d.tags.map((t) => <span key={t} className="tag">{t}</span>) : <span className="muted">No tags</span>}</div>
              <div className="secondary" style={{ whiteSpace: 'pre-wrap' }}>{d.notes || <span className="muted">No notes</span>}</div>
            </>
          )}
        </Panel>
      </div>

      {CHARTS.some((c) => series(c.metric).length > 0 || m[c.metric] !== undefined) ? (
        <div className="chart-grid">
          {CHARTS.filter((c) => m[c.metric] !== undefined || series(c.metric).length > 0).map((c) => (
            <TimeSeriesChart
              key={c.metric}
              title={c.title}
              unit={c.unit}
              format={c.format}
              points={series(c.metric)}
              thresholds={thresholdsFor(rules.data ?? [], d.deviceId, c.metric)}
              refetching={readings.isFetching}
            />
          ))}
        </div>
      ) : null}

      <div className="grid-2">
        <Panel title="Open alerts for this sensor" flush>
          {deviceAlerts.length === 0 ? <div className="empty">No open alerts</div> : (
            <ul className="list">
              {deviceAlerts.map((a) => (
                <li key={a.alertId} className="list__item">
                  <SeverityBadge severity={a.severity} />
                  <div className="grow"><div>{a.message}</div><div className="small muted">{a.ruleName} · opened <TimeAgo iso={a.openedAt} /></div></div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        <Panel title="Recent events" flush>
          {deviceEvents.length === 0 ? <div className="empty">No events for this sensor yet</div> : (
            <ul className="list">
              {deviceEvents.map((e) => (
                <li key={e.eventId} className="list__item">
                  <span className="list__time"><TimeAgo iso={e.ts} /></span>
                  <div className="grow"><div>{e.title}</div><div className="small muted">{e.source}</div></div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </>
  );
}
